/*
<summary>
   This class component is all about Managing the User Created Inspection Record data
   (ToolManagement/GetUserInspectedList).
   Developer: Aashish Singh, Created Date: 27-Aug-2026
</summary>
<param>No Parameter Passed</param>
<returns>Returns class instance</returns>
*/

import { action, computed, makeObservable, observable } from 'mobx';
import toast from 'react-hot-toast';
import { mockUserInspectedListResponse } from '../../constants/mock-data';
import URLConstants from '../../constants/url-constants';
import preferencesStore from './preferences-store';
import { ISharedInspectionFilter } from '../../models/ISharedInspectionFilter';
import IApiResponse, {
  IApiSuccessResponse,
} from '../../models/response/IApiResponse';
import { IUserInspectedListVM } from '../../models/response/IUserInspectedListResponse';
import { formatMessage } from '../../translations/format-message';
import {
  getUserInspectedListInitialState,
  ObservableInitialState,
} from '../initialState';
import * as baseService from '../service/base-service';
import { downloadCsvFromBase64 } from './utils/download-csv';
import { buildInspectionFilterQuery } from './utils/inspection-filter-query';
import { getTenantIdFromLocalStorage } from '../../helpers/localstorage-helper';

export class UserInspectedListStore {
  initialStateValue = ObservableInitialState;
  userInspectedList: IUserInspectedListVM = getUserInspectedListInitialState;
  getAllUserInspectedListState = { ...this.initialStateValue };
  exportUserInspectedListState = { ...this.initialStateValue };

  constructor() {
    makeObservable(this, {
      userInspectedList: observable,
      getAllUserInspectedListState: observable,
      exportUserInspectedListState: observable,
      GetUserInspectedListService: action,
      ExportUserInspectedListService: action,
      resetExportUserInspectedListState: action,
      reset: action,
      getUserInspectedList: computed,
      resetStore: action,
    });
  }

  /**
  * This function is used to get the User Created Inspection Record list from the real
  * backend API (ToolManagement/GetUserInspectedList) by page number.
  * @param currentPage: Current page number
  * @param pageLimit: Number of records per page
  * @param filter: Shared inspection filter values (Owner Factory, Tool No, Tool Name,
  * Inspection Date range, Judgment Result)
  */
  GetUserInspectedListService = (
    currentPage: number,
    pageLimit: number,
    filter?: ISharedInspectionFilter
  ) => {
    this.getAllUserInspectedListState.inProgress = true;
    const url = `${URLConstants.GetUserInspectedList}?PageNo=${currentPage}&PageSize=${pageLimit}${buildInspectionFilterQuery(filter)}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<IUserInspectedListVM>>) => {
        if (response.data.Error) {
          this.getAllUserInspectedListState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.userInspectedList = response.data.Data;
        }
      })
      .catch(() => {
        // Fall back to mock data instead of leaving the page blank when the API is
        // unreachable (e.g. local dev without the backend running).
        this.userInspectedList =
          mockUserInspectedListResponse.Data as IUserInspectedListVM;
      })
      .finally(
        action(() => {
          this.getAllUserInspectedListState.inProgress = false;
        })
      );
  };

  /**
  * This computed property is used to map userInspectedList to the list suitable for the
  * Grid Component.
  */
  get getUserInspectedList() {
    return this.userInspectedList?.ToolCheckInfoMasterLists || [];
  }

  /**
  * This function is used to export the User Created Inspection Record list as a CSV file
  * from the real backend API (ToolManagement/ExportUserInspectedListCSV).
  * @param filter: Shared inspection filter values applied to the export
  */
  ExportUserInspectedListService = (filter?: ISharedInspectionFilter) => {
    this.exportUserInspectedListState.inProgress = true;
    const filterQuery = buildInspectionFilterQuery(filter);
    const languageQuery = `Language=${encodeURIComponent(preferencesStore.getLanguage)}`;
    const tenantIdQuery = `&TenantId=${Number(getTenantIdFromLocalStorage())}`;
    const url = `${URLConstants.ExportUserInspectedList}?${languageQuery}${filterQuery}${tenantIdQuery}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<string>>) => {
        if (response.data.Error) {
          this.exportUserInspectedListState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.exportUserInspectedListState.success = true;
          if (response.data.Data) {
            downloadCsvFromBase64(response.data.Data, 'UserCreatedInspectionRecord');
          }
        }
      })
      .catch((err: any) => {
        this.exportUserInspectedListState.error = err;
        toast.error(formatMessage(err));
      })
      .finally(
        action(() => {
          this.exportUserInspectedListState.inProgress = false;
        })
      );
  };

  /**
  * This function is used to reset exportUserInspectedListState observables to their initial values.
  */
  resetExportUserInspectedListState = () => {
    this.exportUserInspectedListState = { ...this.initialStateValue };
  };

  /**
  * This function is used to reset userInspectedList observables to their initial values.
  */
  reset = () => {
    this.userInspectedList = getUserInspectedListInitialState;
  };

  /**
  * This function is used to reset all store observables to their initial values.
  */
  resetStore = () => {
    this.userInspectedList = getUserInspectedListInitialState;
    this.getAllUserInspectedListState = { ...this.initialStateValue };
    this.exportUserInspectedListState = { ...this.initialStateValue };
  };
}

export default new UserInspectedListStore();
